import { ImagiseError } from './console';

export const JSON_NAMESPACE = 'json';
export const SVG_NAMESPACE = 'svg';

type Namespace = typeof JSON_NAMESPACE | typeof SVG_NAMESPACE;

export type ClassLike<T = unknown> = {
  new (...args: never[]): T;
  type?: string;
  fromObject?: (
    serialized: Record<string, unknown>,
    options: { signal: AbortSignal },
  ) => unknown;
};

export class ClassRegistry {
  private readonly registry: Record<Namespace, Map<string, ClassLike>> = {
    [JSON_NAMESPACE]: new Map<string, ClassLike>(),
    [SVG_NAMESPACE]: new Map<string, ClassLike>(),
  };

  has(classType: string): boolean {
    return this.registry[JSON_NAMESPACE].has(classType);
  }

  getClass<T>(classType: string): ClassLike<T> {
    const classConstructor = this.registry[JSON_NAMESPACE].get(classType);
    if (!classConstructor) {
      throw new ImagiseError(`No class registered for ${classType}`);
    }
    return classConstructor as ClassLike<T>;
  }

  setClass(classConstructor: ClassLike, classType?: string): void {
    if (classType) {
      this.registry[JSON_NAMESPACE].set(classType, classConstructor);
      return;
    }

    if (!classConstructor.type) {
      throw new ImagiseError('Class constructor must have a "type" property when classType is omitted');
    }

    this.registry[JSON_NAMESPACE].set(classConstructor.type, classConstructor);
    this.registry[JSON_NAMESPACE].set(classConstructor.type.toLowerCase(), classConstructor);
  }

  getSvgClass(svgTagName: string): ClassLike | undefined {
    return this.registry[SVG_NAMESPACE].get(svgTagName);
  }

  setSvgClass(classConstructor: ClassLike, svgTagName?: string): void {
    const key = svgTagName ?? classConstructor.type?.toLowerCase();
    if (!key) {
      throw new ImagiseError('SVG class registration requires svgTagName or classConstructor.type');
    }
    this.registry[SVG_NAMESPACE].set(key, classConstructor);
  }
}

export const classRegistry = new ClassRegistry();
